// Diálogo de configuración de alertas.
// - Fiscal / delegados: editan la config GLOBAL (visible para Fiscal y todos
//   los delegados; genera notificaciones in-app).
// - Director / subdirector: edita su config PERSONAL (solo afecta a sus
//   propias alertas del Dashboard; no genera notificaciones porque es vista
//   privada). Sus umbrales pueden quedar distintos a la global del Fiscal.
//
// El backend decide el scope automáticamente según el rol del usuario; el
// front no manda el scope, solo lee el indicador `scope.esArea` del response
// para mostrar el contexto correcto.

import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Switch, TextField, Stack, Divider, Alert, CircularProgress, Chip,
} from '@mui/material';
import { dashEjecAlertasJWT, dashEjecActualizarAlertaJWT } from '../../api/endpoints';

const PRIMARY = '#1769aa';

export default function AlertasConfigDialog({ open, onClose, onChange }) {
    const [config, setConfig] = useState([]);
    const [meta, setMeta] = useState({});
    const [scope, setScope] = useState({ esArea: false });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // Estado local editado (antes de guardar). Mapa por tipo.
    const [edit, setEdit] = useState({});

    useEffect(() => {
        if (!open) return;
        let cancel = false;
        setLoading(true);
        setError('');
        dashEjecAlertasJWT()
            .then(r => {
                if (cancel) return;
                if (r?.ok) {
                    const list = r.data.alertas || [];
                    setConfig(list);
                    setMeta(r.data.meta || {});
                    setScope(r.data.scope || { esArea: false });
                    const initEdit = {};
                    list.forEach(a => { initEdit[a.tipo] = { umbral: a.umbral, activa: a.activa }; });
                    setEdit(initEdit);
                } else {
                    setError(r?.mensaje || 'Error al cargar alertas');
                }
            })
            .finally(() => { if (!cancel) setLoading(false); });
        return () => { cancel = true; };
    }, [open]);

    const setCampo = (tipo, campo, valor) => {
        setEdit(prev => ({ ...prev, [tipo]: { ...prev[tipo], [campo]: valor } }));
    };

    const cambiosPendientes = config.some(a => {
        const e = edit[a.tipo];
        return e && (e.umbral !== a.umbral || e.activa !== a.activa);
    });

    const guardar = async () => {
        setSaving(true);
        setError('');
        try {
            // Solo guardamos los que cambiaron
            const cambios = config.filter(a => {
                const e = edit[a.tipo];
                return e && (e.umbral !== a.umbral || e.activa !== a.activa);
            });
            for (const a of cambios) {
                const e = edit[a.tipo];
                const r = await dashEjecActualizarAlertaJWT(a.tipo, Number(e.umbral), e.activa);
                if (!r?.ok) throw new Error(r?.mensaje || 'Error al actualizar');
            }
            onChange && onChange();
            onClose && onClose();
        } catch (e) {
            setError(e.message || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '4px' } }}>
            <DialogTitle sx={{ color: PRIMARY, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                Configurar alertas del Dashboard
                {scope.esArea && (
                    <Chip size="small" label="Mi configuración (área)" color="primary" variant="outlined" />
                )}
            </DialogTitle>
            <DialogContent dividers>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : (
                    <>
                        <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
                            Una alerta se dispara cuando la métrica que vigila supera (o cae bajo) el umbral configurado.
                            Las alertas desactivadas no aparecen en el banner.
                            {scope.esArea
                                ? ' Esta configuración es solo para tu vista del Dashboard (filtrada por tu área) y no afecta al resto de los usuarios.'
                                : ' Esta configuración es compartida con el Fiscal y los delegados, y genera notificaciones in-app cuando una alerta dispara.'}
                        </Typography>
                        <Stack spacing={2} divider={<Divider />}>
                            {config.map(a => {
                                const m = meta[a.tipo] || {};
                                const e = edit[a.tipo] || { umbral: a.umbral, activa: a.activa };
                                const operador = m.direccion === 'lt' ? 'Menor que' : 'Mayor que';
                                const sufijo = m.unidad === 'percent' ? '%' : '';
                                return (
                                    <Box key={a.tipo}>
                                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                                            <Typography sx={{ fontWeight: 600, color: '#333' }}>{m.nombre}</Typography>
                                            <Switch
                                                checked={e.activa}
                                                onChange={ev => setCampo(a.tipo, 'activa', ev.target.checked)}
                                                color="primary"
                                            />
                                        </Stack>
                                        <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 1 }}>
                                            {m.descripcion}
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1.5}>
                                            <Typography variant="caption" sx={{ color: '#555' }}>
                                                Disparar cuando valor sea {operador.toLowerCase()}:
                                            </Typography>
                                            <TextField
                                                size="small"
                                                type="number"
                                                value={e.umbral}
                                                onChange={ev => setCampo(a.tipo, 'umbral', ev.target.value)}
                                                disabled={!e.activa}
                                                sx={{ width: 100 }}
                                                InputProps={{ endAdornment: sufijo && <Typography variant="caption">{sufijo}</Typography> }}
                                            />
                                            <Typography variant="caption" sx={{ color: '#999' }}>
                                                Valor actual: <strong>{a.valorActual}{sufijo}</strong>
                                            </Typography>
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Stack>
                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancelar</Button>
                <Button onClick={guardar} variant="contained" disabled={saving || !cambiosPendientes || loading}
                    sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#0d4f86' }, textTransform: 'none' }}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
